import { ClientTag, Marker, UserProfile } from '@brands/services/identity/types/UserProfile';

/**
 * A single client-tag assignment, normalized across the two shapes the identity
 * API answers with. `id` is the client tag's own id (absent when the assignment
 * only came back as a marker); `code` is the tag code.
 */
export type UserClientTagRef = { id?: number; code?: string; name?: string };

export type ClientTagOptionLike = { value?: string; label: string; id?: number };

/**
 * A user's assigned client tags, preferring `clients` — which carries the tag's
 * id, name and code — and falling back to the older `markers` shape, where only
 * the tag code is available (in `marker.value`). Screens go through this instead
 * of picking a field, so endpoints that still answer with markers keep working.
 */
export const getUserClientTagRefs = (user?: Pick<UserProfile, 'clients' | 'markers'> | null): UserClientTagRef[] => {
  if (user?.clients?.length) {
    return user.clients.map((client: ClientTag) => ({ id: client.id, code: client.code, name: client.name }));
  }
  return (user?.markers ?? []).map((marker: Marker) => ({ code: marker.value }));
};

/**
 * Whether a client-tag select option points at the same tag as `ref`. Matches on
 * id when both sides have one (an option's `id` is the tag id, not a marker id)
 * and on the tag code otherwise, so marker-sourced refs still resolve.
 */
export const isClientTagOption = (
  ref: UserClientTagRef,
  option?: { id?: string | number; value?: unknown } | null,
): boolean => {
  if (!option) return false;
  if (ref.id != null && option.id != null && Number(option.id) === Number(ref.id)) return true;
  return Boolean(ref.code) && option.value === ref.code;
};

/** True when a form's selected client-tag options are exactly the user's current assignment. */
export const clientTagSelectionMatches = (
  refs: UserClientTagRef[],
  options: ReadonlyArray<{ id?: string | number; value?: unknown }>,
): boolean =>
  refs.length === options.length && refs.every((ref) => options.some((option) => isClientTagOption(ref, option)));

/** Turns assignments into select options, filling in name/code/id from the client-tag catalog. */
export const toClientTagOptions = (refs: UserClientTagRef[], catalog: ClientTag[] = []): ClientTagOptionLike[] =>
  refs.map((ref) => {
    const tag = catalog.find((candidate) =>
      ref.id != null ? candidate.id === ref.id : Boolean(ref.code) && candidate.code === ref.code,
    );
    return { value: ref.code ?? tag?.code, label: ref.name || tag?.name || '', id: ref.id ?? tag?.id };
  });

/**
 * Read-only select options for a locked field: every assigned tag or division, labelled by its
 * own name (or tag code, for marker-only refs) since the viewer's catalog may not include it.
 */
export const toLockedSelectOptions = (
  items: ReadonlyArray<{ id?: string | number; name?: string; code?: string }> = [],
): { id: string; value: string; label: string }[] =>
  items
    .filter((item) => item.id != null || Boolean(item.code))
    .map((item) => {
      const key = String(item.id ?? item.code);
      return { id: key, value: key, label: item.name || item.code || '' };
    });

/**
 * True when a user's tags are known only from the legacy `markers` shape. A marker
 * carries no client-tag id, so matching it against a form option proves only that
 * the codes line up — not that the backend has the assignment stamped on `clients`.
 */
export const hasOnlyLegacyClientTagMarkers = (user?: Pick<UserProfile, 'clients' | 'markers'> | null): boolean =>
  !user?.clients?.length && Boolean(user?.markers?.length);

/**
 * Whether a save still has to write the client-tag selection. An unchanged selection
 * is skipped only when the user's tags came back on `clients`; a markers-only user
 * always writes, so the backend gets the chance to stamp `clients`.
 */
export const clientTagSelectionNeedsSave = (
  user: Pick<UserProfile, 'clients' | 'markers'> | null | undefined,
  options: ReadonlyArray<{ id?: string | number; value?: unknown }>,
): boolean => hasOnlyLegacyClientTagMarkers(user) || !clientTagSelectionMatches(getUserClientTagRefs(user), options);
